import { useAccount, useNetwork, useSwitchNetwork } from 'wagmi';
import { bsc, bscTestnet, sepolia, avalanche, avalancheFuji } from 'viem/chains';

// Abstract chain IDs
const ABSTRACT_TESTNET_ID = 11124;
const ABSTRACT_MAINNET_ID = 2741;

// Arbitrum chain IDs
const ARBITRUM_SEPOLIA_ID = 421614;
const ARBITRUM_ONE_ID = 42161;

// Base chain IDs
const BASE_MAINNET_ID = 8453;
const BASE_SEPOLIA_ID = 84532;

// OP chain IDs
const OP_MAINNET_ID = 10;
const OP_SEPOLIA_ID = 11155420;

// Polygon chain IDs
const POLYGON_MAINNET_ID = 137;
const POLYGON_AMOY_ID = 80002;

// Sei chain IDs
const SEI_TESTNET_ID = 1328;
const SEI_MAINNET_ID = 1329;

// opBNB chain IDs
const OPBNB_MAINNET_ID = 204;
const OPBNB_TESTNET_ID = 5611;

export const useWalletState = () => {
  const { address, isConnected } = useAccount();
  const { chain } = useNetwork();
  const { switchNetwork, chains, isLoading } = useSwitchNetwork();

  const isCorrectChain = chain?.id === bsc.id || 
                        chain?.id === bscTestnet.id || 
                        chain?.id === sepolia.id || 
                        chain?.id === avalanche.id || 
                        chain?.id === avalancheFuji.id ||
                        chain?.id === ABSTRACT_TESTNET_ID ||
                        chain?.id === ABSTRACT_MAINNET_ID ||
                        chain?.id === ARBITRUM_SEPOLIA_ID ||
                        chain?.id === ARBITRUM_ONE_ID ||
                        chain?.id === BASE_MAINNET_ID ||
                        chain?.id === BASE_SEPOLIA_ID ||
                        chain?.id === OP_MAINNET_ID ||
                        chain?.id === OP_SEPOLIA_ID ||
                        chain?.id === POLYGON_MAINNET_ID ||
                        chain?.id === POLYGON_AMOY_ID ||
                        chain?.id === SEI_TESTNET_ID ||
                        chain?.id === SEI_MAINNET_ID ||
                        chain?.id === OPBNB_MAINNET_ID ||
                        chain?.id === OPBNB_TESTNET_ID;

  const isTestnet = chain?.id === bscTestnet.id || 
                   chain?.id === sepolia.id || 
                   chain?.id === avalancheFuji.id ||
                   chain?.id === ABSTRACT_TESTNET_ID ||
                   chain?.id === SEI_TESTNET_ID ||
                   chain?.id === ARBITRUM_SEPOLIA_ID ||
                   chain?.id === BASE_SEPOLIA_ID ||
                   chain?.id === OP_SEPOLIA_ID ||
                   chain?.id === POLYGON_AMOY_ID ||
                   chain?.id === OPBNB_TESTNET_ID;

  const networkName = chain?.name || 'Unknown Network';

  const switchToCorrectChain = async () => {
    if (!isCorrectChain && switchNetwork) {
      try {
        // Try switching to testnet first if available
        if (chains.some(c => c.id === sepolia.id)) {
          await switchNetwork(sepolia.id);
        } else if (chains.some(c => c.id === bscTestnet.id)) {
          await switchNetwork(bscTestnet.id);
        } else if (chains.some(c => c.id === avalancheFuji.id)) {
          await switchNetwork(avalancheFuji.id);
        } else {
          // Fallback to mainnet
          await switchNetwork(bsc.id);
        }
      } catch (error) {
        console.error('Failed to switch network:', error);
        // If switching fails, try adding the network first
        try {
          const networkParams = {
            sepolia: {
              chainId: `0x${sepolia.id.toString(16)}`,
              chainName: 'Sepolia',
              nativeCurrency: {
                name: 'ETH',
                symbol: 'ETH',
                decimals: 18
              },
              rpcUrls: ['https://rpc.sepolia.org'],
              blockExplorerUrls: ['https://sepolia.etherscan.io']
            },
            avalancheFuji: {
              chainId: `0x${avalancheFuji.id.toString(16)}`,
              chainName: 'Avalanche Fuji',
              nativeCurrency: {
                name: 'AVAX',
                symbol: 'AVAX',
                decimals: 18
              },
              rpcUrls: ['https://api.avax-test.network/ext/bc/C/rpc'],
              blockExplorerUrls: ['https://testnet.snowtrace.io']
            }
          };

          await window.ethereum?.request({
            method: 'wallet_addEthereumChain',
            params: [networkParams.avalancheFuji]
          });
        } catch (addError) {
          console.error('Failed to add network:', addError);
        }
      }
    }
  };

  return {
    address,
    isConnected,
    isCorrectChain,
    switchToCorrectChain,
    chainId: chain?.id,
    networkName,
    isTestnet,
    isLoading,
  };
};

export default useWalletState;
